/* eslint-disable @typescript-eslint/unbound-method */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router';

import { UserDO, RolePermissionDO } from 'wdna-components';
import { messageError } from 'wdna-components/lib/helpers/notification';

import Auth from '../../../services/auth/Auth';
import { loginUser } from '../../../services/auth/requests';
import { updateUserInfo } from '../../../services/user/actions';

import {
  version as currentVersion
} from '../../../../../package.json';

interface Props extends RouteComponentProps {
  user?: UserDO;
}

interface State {
  type?: string;
  email?: string;
  password?: string;
  loading?: boolean;
  clicksOnImage?: number;
  adminToggle?: boolean;
}

class Login extends React.PureComponent<Props, State> {
  public constructor(props: Props) {
    super(props);

    this.state = {
      type: 'cli',
      email: '',
      password: '',
      loading: false,
      clicksOnImage: 0,
      adminToggle: false,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  public handleAdmin = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = e.target;
    this.setState({
      [name]: checked ? 'adm' : 'cli',
    });
  };

  public handleFormSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const { type, email, password } = this.state;

    if (type && email && password) {
      this.loadingStart();
      loginUser(type, email, password)?.then((resp: UserDO) => {
        this.onLoginSuccess(resp);
      }).catch((error: any) => {
        this.onLoginFailed(error);
      });
    } else {
      messageError('Error', 'Missing username or password');
    }
  }

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, type, value, checked } = e.target;
    this.setState({
      [name]: type === 'checkbox' ? checked : value,
    });
  }

  public landing = (): string => {
    const user: UserDO | null = Auth.getUser();

    if (user && user.type === 'cli') {
      const permissions: RolePermissionDO[] = user?.role?.role_permissions || [];
      if (permissions) {
        const { permission } = permissions[0].permission || {};
        if (permission === 'dashboard_waste') return '/dashboard/waste';
        if (permission === 'dashboard_meteo') return '/dashboard/meteo';
        if (permission === 'dashboard_gtracking') return '/dashboard/gtracking';
        if (permission === 'dashboard_gposition') return '/dashboard/gposition';
        return '/dashboard/fboard';
      }
    }

    return '/';
  }

  public showAdminToggle = (): void => {
    const { clicksOnImage } = this.state;
    if (clicksOnImage && clicksOnImage < 2) {
      this.setState((prevState: State) => ({
        clicksOnImage: (prevState.clicksOnImage || 0) + 1,
      }));
      return;
    }
    this.setState((prevState: State) => ({
      clicksOnImage: 0,
      adminToggle: !prevState.adminToggle,
    }));
  };

  public onLoginSuccess = (user: UserDO): void => {
    if (!user) return;

    Auth.authenticateUser(user);
    updateUserInfo(user).catch(() => { /* pass */ });
  };

  public onLoginFailed = (error: any): void => {
    this.loadingFinish();

    if (error.message) {
      messageError('Error', error.message);
    } else {
      messageError('Error', (error?.code === 401 ? 'Unauthorized Access' : 'Unknown error'));
    }
  };

  public loadingStart = (): void => {
    this.setState({ loading: true });
  };

  public loadingFinish = (): void => {
    this.setState({ loading: false });
  };

  public render(): JSX.Element {
    const { loading } = this.state;

    if (Auth.isUserAuthenticated()) {
      const landing: string = this.landing();
      return <Redirect to={landing} />;
    }

    const site: string = window.location.hostname.replace('.sensorpatrol.com', '');
    document.title = `SensorPatrol - ${site.toUpperCase()}`;

    return (
      <div id="extr-page">
        <div id="main" role="main" className="animated fadeInDown">
          <div id="content" className="container login-layer">
            <div className="col-xs-12 col-sm-6 col-md-6 col-lg-3 login-box">
              <form id="login-form" className="smart-form" onSubmit={this.handleFormSubmit}>
                <header>
                  <div id="login-logo" role="presentation" onDoubleClick={this.showAdminToggle}>
                    <b>{site}</b>
                    <br />by<br />
                    <img src="assets/img/logos/logo_negativo.png" alt="logo" />
                    <h1>{currentVersion}</h1>
                  </div>
                </header>
                <fieldset>
                  <section>
                    <label className="input">
                      <input
                        type="email"
                        name="email"
                        value={this.state.email}
                        placeholder="Email"
                        onChange={this.handleChange}
                      />
                    </label>
                  </section>
                  <section>
                    <label className="input">
                      <input
                        type="password"
                        name="password"
                        value={this.state.password}
                        placeholder="Password"
                        onChange={this.handleChange}
                      />
                    </label>
                  </section>
                  <div hidden={!this.state.adminToggle}>
                    <br />
                    <section className="check-login">
                      <label className="toggle remember">
                        <input type="checkbox" name="type" onChange={this.handleAdmin} defaultChecked={false} />
                        <i data-swchon-text="ON" data-swchoff-text="OFF" /> <span>Login as admin</span>
                      </label>
                    </section>
                  </div>
                </fieldset>
                <footer>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading && <i className="fa fa-refresh fa-spin login-loader" />} Log in
                  </button>
                </footer>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any, ownProps: Props): Props => {
  return {
    ...ownProps,
    user: state.user,
  };
};

export default withRouter(connect(mapStateToProps)(Login));
